import { ReactElement, useState } from "react";
import type { GetStaticProps } from "next";
import NextHeadSeo from "next-head-seo";

import LayoutMain from "~/components/layouts/LayoutMain";
import { restApiESGetSettings } from "~/utils/restApi";
import PageMargins from "~/components/styled/PageMargins";
import { Pagination } from "~/components/ui/Pagination";
import { useConfigContext } from "~/providers/ConfigContextProvider";
import { VideosGrid } from "~/components/styled/VideoGrid";
import ListItemVideoOrAudio from "~/components/content/ListItemVideoOrAudio";
import { HeaderWithFilter } from "~/components/partials/HeaderWithFilter";
import {
  MediaFilter,
  FILTER_BASE_URL,
  FilterData,
  defaultFilterData,
} from "~/components/partials/MediaFilter";

import { MediaFilterListInfo } from "~/components/partials/MediaFilterListInfo";
import { createESQueryNextPageBase } from "~/utils/createESQueryNextPageBase";
import useRestCachedESQuery from "~/hooks/restApi/useRestCachedESQuery";

const WhatsOnFilterList = () => {
  const config = useConfigContext();

  const [filterQuery, setFilterQuery] = useState("");
  const [isFilterOpen, setIsFilterOpen] = useState(true);

  const [filterData, setFilterData] = useState<FilterData>({
    ...defaultFilterData,
  });

  const nextPageBase = createESQueryNextPageBase(
    `${config.baseUrl}${FILTER_BASE_URL}`
  );

  const { isLoading, isError, queryResult } = useRestCachedESQuery(filterQuery);

  let title = "Media";
  if (filterData?.type?.length === 1) {
    title = filterData?.type[0] === "video" ? "Videos" : "Audios";
  }

  return (
    <>
      <NextHeadSeo title={`Media - ${config.appTitle}`} />
      <HeaderWithFilter
        title={title}
        controlsId="media-filter"
        {...{
          isFilterOpen,
          setIsFilterOpen,
        }}
      />
      <main id="content">
        <MediaFilter
          id="media-filter"
          initiallyOpen={true}
          {...{
            isFilterOpen,
            filterData,
            setFilterData,
            filterQuery,
            setFilterQuery,
          }}
        />

        <MediaFilterListInfo
          {...{
            isLoading,
            isError,
            queryResult,
            filterData,
          }}
        />

        {queryResult?.items?.length > 0 && (
          <>
            <PageMargins
              spaceBottom={
                queryResult?.pageTotal && queryResult?.pageTotal > 1 ? 6 : 5
              }
            >
              <VideosGrid>
                {queryResult?.items.map((item: any, index: number) => (
                  <ListItemVideoOrAudio item={item} key={`post-${index + 1}`} />
                ))}
              </VideosGrid>
            </PageMargins>
            {filterData.page &&
              queryResult?.postTotal &&
              queryResult?.pageTotal > 1 && (
                <PageMargins spaceBottom={5}>
                  <Pagination
                    firstPageShowMore="Show more videos"
                    {...{
                      page: filterData.page,
                      postTotal: queryResult?.postTotal,
                      pageTotal: queryResult?.pageTotal,
                    }}
                    paginationBaseUrl={nextPageBase}
                  />
                </PageMargins>
              )}
          </>
        )}
      </main>
    </>
  );
};

export const getStaticProps: GetStaticProps = async (context) => {
  const frontendSettings = await restApiESGetSettings(["mediaCategory"]);
  return {
    props: {
      frontendSettings,
    },
    revalidate: frontendSettings?.options?.copyrightNotice ? 60 : 15,
  };
};

WhatsOnFilterList.getLayout = function getLayout(page: ReactElement) {
  return <LayoutMain>{page}</LayoutMain>;
};
export default WhatsOnFilterList;
